import { useEffect, useMemo, useRef } from 'react';
import type { Map as LeafletMap } from 'leaflet';
import {
  CircleMarker,
  LayersControl,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from 'react-leaflet';
import { Focus, MapPinOff, Maximize2, Route } from 'lucide-react';
import {
  canonicalLevelColorScheme,
  jumpCourseSuperfix,
  type Jump,
  type JumpGroup,
  type Level,
  type LevelSlug,
} from '@wht-xc-utils/shared/domain';
import { buildJudgingPostRoute } from '@wht-xc-utils/shared/scheduling';
import { JumpIndicators } from './JumpIndicators';

interface JudgingPostMapProps {
  group: JumpGroup | null;
  jumps: Jump[];
  levels: Level[];
  contextJumps?: Jump[];
}

const DEFAULT_CENTER: [number, number] = [37.4201159, -122.2341905];
const FALLBACK_COLORS = {
  textColor: '#ffffff',
  backgroundColor: '#365a48',
};
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const CARTO_ATTRIBUTION = `${OSM_ATTRIBUTION} &copy; <a href="https://carto.com/attributions">CARTO</a>`;
const ESRI_IMAGERY_ATTRIBUTION =
  'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';
const STADIA_ATTRIBUTION = `${OSM_ATTRIBUTION} &copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://stamen.com/">Stamen Design</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a>`;
const MAPTILER_ATTRIBUTION = `${OSM_ATTRIBUTION} &copy; <a href="https://www.maptiler.com/">MapTiler</a>`;
const cartoApiKey = import.meta.env.VITE_CARTO_API_KEY?.trim();
const stadiaMapsApiKey = import.meta.env.VITE_STADIA_MAPS_API_KEY?.trim();
const mapTilerApiKey = import.meta.env.VITE_MAPTILER_API_KEY?.trim();

export function JudgingPostMap({ group, jumps, levels, contextJumps = [] }: JudgingPostMapProps) {
  const mapRef = useRef<LeafletMap | null>(null);
  const route = useMemo(
    () => (group ? buildJudgingPostRoute(group, jumps, levels) : null),
    [group, jumps, levels],
  );
  const routeJumpIds = useMemo(
    () => new Set(route?.stops.map((stop) => stop.jumpId) ?? []),
    [route],
  );
  const overviewPoints = useMemo(
    () => contextJumps.filter((jump) => jump.latitude != null && jump.longitude != null),
    [contextJumps],
  );
  const contextPoints = useMemo(
    () => overviewPoints.filter((jump) => !routeJumpIds.has(jump.id)),
    [overviewPoints, routeJumpIds],
  );
  const positions =
    route?.stops.map((stop) => [stop.latitude, stop.longitude] as [number, number]) ?? [];
  const fitPositions = useMemo(
    () => overviewPoints.map((jump) => [jump.latitude!, jump.longitude!] as [number, number]),
    [overviewPoints],
  );
  const center = fitPositions[0] ?? DEFAULT_CENTER;
  const labelCandidates =
    route?.stops.flatMap((stop) =>
      stop.levelSlug
        ? [
            {
              canonicalSlug: stop.levelSlug,
              number: stop.jumpNumber,
              name: stop.jumpName,
              latitude: stop.latitude,
              longitude: stop.longitude,
            },
          ]
        : [],
    ) ?? [];

  if (!group) {
    return (
      <div className="map-empty-state">
        <Route aria-hidden="true" />
        <strong>Select a judging post</strong>
        <span>Its jump sequence and between-division transfer line will appear here.</span>
      </div>
    );
  }

  if (!route || (route.stops.length === 0 && overviewPoints.length === 0)) {
    return (
      <div className="map-empty-state">
        <MapPinOff aria-hidden="true" />
        <strong>No mapped jump coordinates</strong>
        <span>Fetch course geometry for this post's levels to place it on the map.</span>
      </div>
    );
  }

  return (
    <div className="judging-post-map">
      <div className="judging-post-map-frame">
        <MapContainer
          ref={mapRef}
          center={center}
          zoom={16}
          scrollWheelZoom
          preferCanvas={false}
          className="judging-post-map-canvas"
        >
          <LayersControl position="bottomleft">
            {cartoApiKey ? (
              <LayersControl.BaseLayer checked name="CARTO Voyager">
                <TileLayer
                  attribution={CARTO_ATTRIBUTION}
                  url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(cartoApiKey)}`}
                />
              </LayersControl.BaseLayer>
            ) : null}
            <LayersControl.BaseLayer checked={!cartoApiKey} name="Esri World Imagery">
              <TileLayer
                attribution={ESRI_IMAGERY_ATTRIBUTION}
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Esri World Imagery · Grayscale">
              <TileLayer
                attribution={ESRI_IMAGERY_ATTRIBUTION}
                className="grayscale-map-tiles"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            {stadiaMapsApiKey ? (
              <LayersControl.BaseLayer name="Print · Stamen Toner Lite">
                <TileLayer
                  attribution={STADIA_ATTRIBUTION}
                  url={`https://tiles.stadiamaps.com/tiles/stamen_toner_lite/{z}/{x}/{y}{r}.png?api_key=${encodeURIComponent(stadiaMapsApiKey)}`}
                />
              </LayersControl.BaseLayer>
            ) : null}
            {mapTilerApiKey ? (
              <LayersControl.BaseLayer name="Print · MapTiler Grayscale">
                <TileLayer
                  attribution={MAPTILER_ATTRIBUTION}
                  url={`https://api.maptiler.com/maps/gray/{z}/{x}/{y}.png?key=${encodeURIComponent(mapTilerApiKey)}`}
                />
              </LayersControl.BaseLayer>
            ) : null}
          </LayersControl>
          <FitMapToPoints positions={fitPositions} />

          {contextPoints.map((jump) => (
            <CircleMarker
              key={jump.id}
              center={[jump.latitude!, jump.longitude!]}
              radius={3}
              pathOptions={{
                color: '#708078',
                fillColor: '#f7f4eb',
                fillOpacity: 0.8,
                weight: 1,
                opacity: 0.55,
              }}
            />
          ))}

          {positions.length > 1 ? (
            <Polyline
              positions={positions}
              pathOptions={{
                color: '#b9683d',
                weight: 4,
                opacity: 0.9,
                dashArray: '9 7',
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
          ) : null}

          {route.stops.map((stop) => {
            const colors = levelColors(stop.levelSlug);
            const superfix = stop.levelSlug
              ? jumpCourseSuperfix(
                  {
                    canonicalSlug: stop.levelSlug,
                    number: stop.jumpNumber,
                    name: stop.jumpName,
                    latitude: stop.latitude,
                    longitude: stop.longitude,
                  },
                  labelCandidates,
                )
              : null;
            const jump = jumps.find((candidate) => candidate.id === stop.jumpId);
            return (
              <CircleMarker
                key={stop.jumpId}
                center={[stop.latitude, stop.longitude]}
                radius={Math.max(11, 7 + stop.jumpNumber.length * 3)}
                pathOptions={{
                  color: colors.textColor,
                  fillColor: colors.backgroundColor,
                  fillOpacity: 1,
                  weight: 3,
                }}
              >
                <Tooltip permanent direction="center" className="post-map-sequence-label">
                  <span style={{ color: colors.textColor }}>
                    {stop.jumpNumber}
                    {superfix ? <sup>{superfix}</sup> : null}
                  </span>
                </Tooltip>
                <Popup>
                  <strong>
                    {stop.levelDisplayName} · Jump {stop.jumpNumber}
                  </strong>
                  {stop.jumpName ? <div>{stop.jumpName}</div> : null}
                  {jump ? <JumpIndicators jump={jump} /> : null}
                  {stop.distanceFromPreviousM != null ? (
                    <div>{Math.round(stop.distanceFromPreviousM)} m from the previous division</div>
                  ) : (
                    <div>First division at this post</div>
                  )}
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        <div className="post-map-controls" aria-label="Map viewport">
          <button
            type="button"
            onClick={() => fitMapToPoints(mapRef.current, fitPositions, 16)}
            disabled={fitPositions.length === 0}
          >
            <Maximize2 aria-hidden="true" />
            Entire event
          </button>
          <button
            type="button"
            onClick={() => fitMapToPoints(mapRef.current, positions, 18)}
            disabled={positions.length === 0}
          >
            <Focus aria-hidden="true" />
            Focus post
          </button>
        </div>
      </div>

      <ol className="post-route-list">
        {route.stops.map((stop) => {
          const colors = levelColors(stop.levelSlug);
          const superfix = stop.levelSlug
            ? jumpCourseSuperfix(
                {
                  canonicalSlug: stop.levelSlug,
                  number: stop.jumpNumber,
                  name: stop.jumpName,
                  latitude: stop.latitude,
                  longitude: stop.longitude,
                },
                labelCandidates,
              )
            : null;
          const jump = jumps.find((candidate) => candidate.id === stop.jumpId);
          return (
            <li key={stop.jumpId}>
              <span
                className="post-route-dot"
                style={{
                  color: colors.textColor,
                  backgroundColor: colors.backgroundColor,
                  borderColor: colors.textColor,
                }}
              >
                {stop.jumpNumber}
                {superfix ? <sup>{superfix}</sup> : null}
              </span>
              <span>
                <strong>{stop.levelDisplayName}</strong>
                <small>
                  Jump {stop.jumpNumber}
                  {superfix ? <sup>{superfix}</sup> : null}
                  {stop.jumpName ? ` · ${stop.jumpName}` : ''}
                  {jump ? <JumpIndicators jump={jump} /> : null}
                </small>
              </span>
              <span className="post-route-distance">
                {stop.distanceFromPreviousM == null
                  ? 'Start'
                  : `${Math.round(stop.distanceFromPreviousM)} m`}
              </span>
            </li>
          );
        })}
      </ol>

      {route.missingCoordinateJumpIds.length > 0 ? (
        <div className="notice">
          {route.missingCoordinateJumpIds.length} jump
          {route.missingCoordinateJumpIds.length === 1 ? '' : 's'} in this post do not have
          coordinates and are omitted from the map.
        </div>
      ) : null}

      <p className="map-guidance">
        The dashed line shows division order and relative transfer distance. It is not a routed road
        or trail direction.
      </p>
    </div>
  );
}

function levelColors(slug: LevelSlug | null | undefined) {
  return slug ? canonicalLevelColorScheme(slug) : FALLBACK_COLORS;
}

function FitMapToPoints({ positions }: { positions: Array<[number, number]> }) {
  const map = useMap();

  useEffect(() => {
    fitMapToPoints(map, positions, 16);
  }, [map, positions]);

  return null;
}

function fitMapToPoints(
  map: LeafletMap | null,
  positions: Array<[number, number]>,
  maxZoom: number,
): void {
  if (!map || positions.length === 0) return;
  if (positions.length === 1) {
    map.setView(positions[0]!, maxZoom);
    return;
  }
  map.fitBounds(positions, { padding: [36, 36], maxZoom });
}
